import { getUseInsurancePolicyPackageQkey } from '@parties/src/features/detail/api/insurance-policies/getInsurancePolicyPackageById';
import { useMutation, useQueryClient } from 'react-query';
import { IdResponse } from '../../../../../../../types/API';
import { InsurancePolicy } from '../../../../../../advisory-report/src/types/AdvisoryReport';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import {
  getUseInsurancePoliciesQKey,
  InsurencePoliciesPartialResponse,
} from './getInsurancePoliciesByParty';
import {
  getUseInsurancePolicyPackagesQKey,
  InsurencePolicyPackagesPartialResponse,
} from './getInsurancePolicyPackagesByParty';

interface Params {
  partyId: string;
  insurancePolicy: InsurancePolicy;
}

const putInsurancePolicy = async ({ partyId, insurancePolicy }: Params) => {
  const { data, isError } = await wgApi.put<IdResponse>(
    `/v1/api/parties/${partyId}/insurance-policies`,
    insurancePolicy,
    { explicitNull: true },
  );
  if (isError) throw new Error();
  return data;
};

export const useUpdateInsurancePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation(putInsurancePolicy, {
    onMutate: async ({ partyId, insurancePolicy }) => {
      const packageId = insurancePolicy.policy_package_id;
      const insurancePoliciesQKey = getUseInsurancePoliciesQKey({ partyId });
      const insurancePackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });

      await queryClient.cancelQueries(insurancePoliciesQKey);
      await queryClient.cancelQueries(insurancePackagesQkey);

      const previousPolicies = queryClient.getQueryData(insurancePoliciesQKey);
      const previousPackages = queryClient.getQueryData(insurancePackagesQkey);

      queryClient.setQueryData(
        insurancePoliciesQKey,
        (prev: InsurencePoliciesPartialResponse | undefined) => {
          const index = prev?.items.findIndex(
            ({ id }) => id === insurancePolicy.id,
          );
          if (index != null && index > -1) {
            const newPolicies = [...(prev?.items || [])];
            newPolicies[index] = insurancePolicy;
            return {
              ...prev,
              items: newPolicies,
            };
          }
          return {
            ...prev,
            items: [...(prev?.items || []), insurancePolicy],
          };
        },
      );

      if (packageId) {
        queryClient.setQueryData(
          insurancePackagesQkey,
          (prev: InsurencePolicyPackagesPartialResponse | undefined) => {
            const packageObj = prev?.items.find(({ id }) => id === packageId);
            if (packageObj) {
              const newPackages = (prev?.items || []).map((pack) => {
                if (pack.id === packageId) {
                  const policyIndex = (pack.insurance_policies || []).findIndex(
                    ({ id }) => id === insurancePolicy.id,
                  );
                  const newPolicies = pack.insurance_policies || [];
                  if (policyIndex > -1) {
                    newPolicies[policyIndex] = insurancePolicy;
                  } else {
                    newPolicies.push(insurancePolicy);
                  }
                  return { ...pack, insurance_policies: newPolicies };
                }
                return pack;
              });
              return {
                ...prev,
                items: newPackages,
              };
            }
            return prev as InsurencePolicyPackagesPartialResponse;
          },
        );
      }

      return { previousPolicies, previousPackages };
    },
    onError: (_, { partyId, insurancePolicy }, ctx) => {
      const insurancePoliciesQKey = getUseInsurancePoliciesQKey({ partyId });
      const insurancePackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });
      queryClient.setQueryData(insurancePoliciesQKey, ctx?.previousPolicies);
      if (insurancePolicy.policy_package_id) {
        queryClient.setQueryData(insurancePackagesQkey, ctx?.previousPackages);
      }
    },
    onSuccess: (_, { partyId, insurancePolicy }) => {
      queryClient.invalidateQueries(getUseInsurancePoliciesQKey({ partyId }));
      if (insurancePolicy.policy_package_id) {
        queryClient.invalidateQueries(
          getUseInsurancePolicyPackagesQKey({ partyId }),
        );
        queryClient.invalidateQueries(
          getUseInsurancePolicyPackageQkey({
            partyId,
            packageId: insurancePolicy.policy_package_id,
          }),
        );
      }
    },
  });
};
